import React, { FC } from 'react';
import { useWatch } from 'react-hook-form';

import { Stack, Text } from '@mantine/core';
import useSWR from 'swr';

import { CollectionFile, ComponentFileType, ComponentSection, FileVisibility } from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';
import { CompanyHelpers } from 'helpers/CompanyHelpers';

import { CollateralTeaser } from 'components/collateral/CollateralTeaser';
import { ComponentChatButton } from 'components/component-chat/components/ComponentChatButton';
import { useComponentChat } from 'components/component-chat/hooks/useComponentChat';
import { ComponentDatasheetSectionProgress } from 'components/component-datasheet/components/ComponentDatasheetSectionProgress';
import { ComponentFilesField } from 'components/component-fields/ComponentFilesField';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { FileGrid } from 'components/files/FileGrid';
import { TeaserCarousel } from 'components/teaser-carousel/TeaserCarousel';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useCurrentUser } from 'hooks/use-current-user';
import { CollateralService } from 'services/CollateralService';

import { useComponentContext } from '../../hooks/use-component-context';
import { ComponentDatasheetSection } from './ComponentDatasheetSection';

const Files: FC = () => {
    const user = useCurrentUser();

    const mode = useDatasheetMode();
    const editMode = mode !== DatasheetMode.VIEW;
    const { toggleChatOpen } = useComponentChat();

    const [files = []] = useWatch({ name: ['files'] }) as [CollectionFile[] | undefined];
    const hasFiles = files.length > 0;
    const downloads = files.filter(
        (file) => file.type !== ComponentFileType.CERTIFICATE && file.visibility !== FileVisibility.PRIVATE,
    );

    const { component } = useComponentContext();
    const { company: manufacturer } = useCompanyProfile(component.manufacturer);
    const { data: pinnedCollateral = [] } = useSWR(
        component.id ? CollateralHelpers.swr.byProduct(component.id) : null,
        () => CollateralService.listByProduct(component.id!),
    );

    const productFileIds = files.map((file) => (typeof file.file === 'object' ? file.file?.id : file.file));
    const collateral = pinnedCollateral.filter(
        (item) => CollateralHelpers.isViewable(item) && !productFileIds.includes(CollateralHelpers.file(item)?.id),
    );
    const showCollateral = !editMode && !!manufacturer && collateral.length > 0;

    if (!editMode && downloads.length === 0 && !showCollateral) return null;

    return (
        <ComponentDatasheetSection
            title="Downloads"
            id={ComponentSection.FILES}
            afterTitle={<ComponentDatasheetSectionProgress section={ComponentSection.FILES} />}
        >
            {user && hasFiles && (
                <ComponentChatButton
                    toggleChatOpen={toggleChatOpen}
                    visibleFrom="sm"
                    style={{
                        pointerEvents: 'all',
                        alignSelf: 'flex-start',
                    }}
                />
            )}
            {editMode ? (
                <ComponentFilesField editable={editMode} />
            ) : (
                downloads.length > 0 && <FileGrid files={downloads} />
            )}
            {showCollateral && (
                <Stack gap="sm" mt="md">
                    <Text fw={600}>Brochures and videos</Text>
                    <TeaserCarousel>
                        {collateral.map((item) => (
                            <CollateralTeaser
                                key={item.id}
                                collateral={item}
                                title={item.title}
                                href={CompanyHelpers.urls.collateralItem(manufacturer.slug, item.id)}
                            />
                        ))}
                    </TeaserCarousel>
                </Stack>
            )}
        </ComponentDatasheetSection>
    );
};

export { Files };
